import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, FormControlLabel, Divider } from "@mui/material";
import CarrierAutocomplete from "../../Inputs/CarrierAutocomplete/CarrierAutocomplete";
import CarrierDotNumberAutocomplete from "../../Inputs/CarrierDotNumberAutocomplete/CarrierDotNumberAutocomplete";
import CarrierMcNumberAutocomplete from "../../Inputs/CarrierMcNumberSearch/CarrierMcNumberAutocomplete";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryCheckbox from "../../Inputs/PrimaryCheckBox/PrimaryCheckBox";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import RateConfirmationModal from "../../Modals/RateConfirmationModal/RateConfirmationModal";
import WarningMessage from "../../WarningMessage/WarningMessage";
import { sortArrayOfStringObjectsAsc } from "../../../utils/sortHelpers";
import { formatPhoneNumber } from "../../../utils/formatHelpers";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";
import { nullCheck } from "../../../utils/dataHelpers";
import { LoadService } from "../../../services/LoadService";
import { CarrierService } from "../../../services/CarrierService";
import { SecurityService } from "../../../services/SecurityService";
import { ShotClockService } from "../../../services/ShotClockService";
import { ActiveUserContext, LockedLoadsContext, AlertContext } from "../../Dashboard/Dashboard";

export default function LoadBookingModal({
  handleRecentlyViewed
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const lockedLoads = useContext(LockedLoadsContext);
  const activeUser = useContext(ActiveUserContext);
  let [searchParams] = useSearchParams();
  const [load, setLoad] = useState({});
  const [carrierId, setCarrierId] = useState("");
  const [carrier, setCarrier] = useState(null);
  const [carrierOfferId, setCarrierOfferId] = useState("");
  const [totalRate, setTotalRate] = useState("");
  const [linehaul, setLinehaul] = useState("");
  const [fuelSurcharge, setFuelSurcharge] = useState("");
  const [dispatchers, setDispatchers] = useState([]);
  const [selectedDispatcher, setSelectedDispatcher] = useState("");
  const [carrierContacts, setCarrierContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [tractorNumber, setTractorNumber] = useState("");
  const [trailerNumber, setTrailerNumber] = useState("");
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [offerComments, setOfferComments] = useState("");
  const [fscPerMile, setFscPerMile] = useState("");
  const [carrierQualified, setCarrierQualified] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [fuelSurchargeValid, setFuelSurchargeValid] = useState(false);
  const [totalRateValid, setTotalRateValid] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadWarningText, setLoadWarningText] = useState("");
  const carrierWarningText = "Possible issues found with carrier.";
  const [sendRateCon, setSendRateCon] = useState(true);
  const [rateConOpen, setRateConOpen] = useState(false);
  const [rateConData, setRateConData] = useState({});
  const [autoLocked, setAutoLocked] = useState(false);

  const checkLoadLock = () => {
    return lockedLoads.find(l => l.id === load.movementId.toString());
  };

  const handleCancel = () => {
    if (autoLocked && checkLoadLock()) {
      ShotClockService.UnlockLoad(instance, load.movementId).then()
        .catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
    }
    setAutoLocked(false);
    handleClose();
  };

  const handleClose = () => {
    window.close();
  };

  const handleConfirmBooking = () => {
    if (!submitDisabled) {
      setIsLoading(true);
      let dispatch = dispatchers.find(({ userIdMcld }) => userIdMcld == selectedDispatcher);
      const contact = carrierContacts.find(({ id }) => id === selectedContactId);
      const payload = {
        movementId: load.movementId.toString(),
        orderId: load.id,
        carrierId: carrier.id,
        carrierOfferId: carrierOfferId,
        rate: linehaul.toString(),
        FSCAmount: fuelSurcharge.toString(),
        userId: selectedDispatcher,
        carrierContact: contact.name,
        carrierPhone: contactPhone,
        contactEmail: contactEmail,
        driver: driverName,
        driverPhone: driverPhone,
        tractorNumber: tractorNumber,
        trailerNumber: trailerNumber,
        carrierPro: confirmationNumber,
        comments: offerComments,
        totalRate: totalRate,
        fscPerMile: fscPerMile,
        contactId: contact.id,
        dispatcher: dispatch.userName,
        dispatcherEmail: dispatch.email,
        dispatcherPhone: "",
        dispatcherFax: "",
        carrierFax: nullCheck(carrier, "", "drsPayee", "fax")
      };

      setRateConData(payload);

      CarrierService.BookLoad(instance, payload).then(res => {
        setIsLoading(false);

        const item = {
          type: "load",
          loadId: load.id,
          status: res.brokerageStatus,
          lane: `${load.originCity} > ${load.destinationCity}`,
          isBookmark: false
        };
        handleRecentlyViewed(item);

        if (sendRateCon) {
          setRateConOpen(true);
        } else {
          handleClose();
        }
      }).catch(error => {
        setIsLoading(false);
        console.error(error);
        warning(error);
      });
    }
  };

  const calculateLinehaulAndFuel = () => {
    const miles = load.miles;
    const fsc = miles * fscPerMile;
    const lh = totalRate - fsc;
    setLinehaul(Math.round((lh + Number.EPSILON) * 100) / 100);
    setFuelSurcharge(Math.round((fsc + Number.EPSILON) * 100) / 100);
  };

  const calculateTotalRate = () => {
    const fsc = parseFloat(fuelSurcharge) || 0;
    const lh = parseFloat(linehaul) || 0;
    setTotalRate(Math.round(((lh + fsc) + Number.EPSILON) * 100) / 100);
  };

  useEffect(() => {
    setSubmitDisabled(
      !carrier ||
      !carrierQualified ||
      !totalRateValid ||
      !fuelSurchargeValid ||
      !selectedDispatcher ||
      !selectedContactId ||
      !contactPhone ||
      !contactEmail ||
      !emailValid ||
      !!loadWarningText
    );
  }, [
    carrier,
    carrierQualified,
    totalRateValid,
    fuelSurchargeValid,
    selectedDispatcher,
    selectedContactId,
    contactPhone,
    contactEmail,
    emailValid,
    loadWarningText
  ]);

  useEffect(() => {
    const contact = carrierContacts.find(({ id }) => id === selectedContactId);
    if (contact) {
      if (contact.primaryPhone === "P" || !contact.primaryPhone) {
        setContactPhone(formatPhoneNumber(contact.phone));
      } else if (contact.primaryPhone === "M") {
        setContactPhone(formatPhoneNumber(contact.mobile));
      }

      setContactEmail(contact.email);
    }
  }, [selectedContactId]);

  useEffect(() => {
    if (fuelSurcharge < 0) {
      setFuelSurchargeValid(false);
    } else {
      setFuelSurchargeValid(true);
    }
  }, [fuelSurcharge]);

  useEffect(() => {
    if (totalRate <= 0) {
      setTotalRateValid(false);
    } else {
      setTotalRateValid(true);
    }

    if (totalRate > 0 && load.miles && fscPerMile) {
      calculateLinehaulAndFuel();
    }
  }, [totalRate, fscPerMile]);

  useEffect(() => {
    if (activeUser?.userIdMcld && dispatchers.length > 0) {
      setSelectedDispatcher(activeUser.userIdMcld);
    }
  }, [dispatchers, activeUser]);

  useEffect(() => {
    if (carrier) {
      setIsLoading(true);
      setCarrierQualified(false);

      CarrierService.GetCarrierContacts(instance, carrier.id).then(res => {
        const sortedContacts = sortArrayOfStringObjectsAsc(res, "name");
        setCarrierContacts(sortedContacts);

        if (sortedContacts.length > 0) {
          const contact = sortedContacts.some(({ isPrimary }) => isPrimary) ? sortedContacts.find(({ isPrimary }) => isPrimary) : sortedContacts[0];
          setSelectedContactId(contact.id);
          if (contact.primaryPhone === "P" || !contact.primaryPhone) {
            setContactPhone(formatPhoneNumber(contact.phone));
          } else if (contact.primaryPhone === "M") {
            setContactPhone(formatPhoneNumber(contact.mobile));
          }
          setContactEmail(contact.email);
        } else {
          setSelectedContactId("");
          setContactPhone("");
          setContactEmail("");
        }
      }).catch(error => {
        console.error(error);
        warning(error);
      });

      CarrierService.GetCarrierQualifications(instance, carrier.id, load.movementId).then(res => {
        setCarrierQualified(res.isQualified);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setCarrierQualified(false);
        setIsLoading(false);
        warning(error);
      });

      CarrierService.GetLoadOffers(instance, load.id).then(res => {
        if (res.offers.length > 0) {
          setOfferComments(res.offers.filter(o => o.carrierId === carrier.id && o.comments != null).map(({ comments }) => comments).join(". "));
        }
      }).catch(error => {
        console.error(error);
        warning(error);
      });

      CarrierService.GetFuelSurcharge(instance).then(res => {
        setFscPerMile(res.surcharge);
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    } else {
      setCarrierContacts([]);
      setSelectedContactId("");
      setContactPhone("");
      setContactEmail("");
    }
  }, [carrier]);

  useEffect(() => {
    if (load?.id) {
      if (!checkLoadLock()) {
        ShotClockService.LockLoad(instance, load.movementId, load.id).then(() => {
          setAutoLocked(true);
        }).catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
      }

      if (load.payeeId) {
        setLoadWarningText("Load is already booked");
      } else {
        setLoadWarningText("");
      }
    }
  }, [load]);

  useEffect(() => {
    const loadId = searchParams.get("loadId");
    if (loadId) {
      setIsLoading(true);
      LoadService.GetLoad(instance, loadId).then(res => {
        setLoad(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }

    if (searchParams.get("carrierId")) {
      setCarrierId(searchParams.get("carrierId"));
    }

    if (searchParams.get("offerAmount")) {
      setTotalRate(searchParams.get("offerAmount"));
    }

    if (searchParams.get("carrierOfferId")) {
      setCarrierOfferId(searchParams.get("carrierOfferId"));
    }

    SecurityService.GetUsers(instance).then(res => {
      const sortedUsers = sortArrayOfStringObjectsAsc(res, "userIdMcld");
      setDispatchers(sortedUsers);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  }, []);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, (submitDisabled || isLoading), handleConfirmBooking)} sx={{ margin: "10px" }}>
      <Box sx={{ marginBottom: "5px" }}>
        <Box sx={{ marginBottom: "5px" }}>
          <Typography variant="h2" color="black_opacity.100">
            Booking Confirmation for #{load?.id}
          </Typography>
        </Box>
        <Divider />
      </Box>
      <Box sx={{ display: "flex", marginBottom: "5px" }}>
        <Box>
          <Box className="Load-modal-input-row">
            <Typography variant="h3" color="primary.60">
              Verify your Carrier
            </Typography>
          </Box>
          <Box className="Load-modal-input-row">
            <CarrierAutocomplete
              title="Carrier *"
              titleSize="subhead2.fontSize"
              width="400px"
              carrierId={carrierId}
              selectedOption={carrier}
              updateSelectedCarrier={setCarrier}
              error={!carrier}
              disabled={isLoading}
            />
          </Box>
          <Box className="Load-modal-input-row">
            <Box>
              <CarrierMcNumberAutocomplete
                title="MC # *"
                titleSize="subhead2.fontSize"
                width="190px"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                error={!carrier}
                disabled={isLoading}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <CarrierDotNumberAutocomplete
                title="DOT # *"
                titleSize="subhead2.fontSize"
                width="190px"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                error={!carrier}
                disabled={isLoading}
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <Box>
              <PrimaryTextField
                label="Total *"
                value={totalRate}
                onChange={setTotalRate}
                onBlur={calculateLinehaulAndFuel}
                width="190px"
                onEnter={handleConfirmBooking}
                isCurrency
                disabled={isLoading}
                error={!totalRateValid || !totalRate}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <FormControlLabel
                control={
                  <Box sx={{ margin: "0 8px" }}>
                    <PrimaryCheckbox
                      checked={sendRateCon}
                      handleChange={e => setSendRateCon(e.target.checked)}
                      color="black_solid.60"
                      disabled={isLoading}
                    />
                  </Box>
                }
                label={
                  <Typography variant="subhead2" color="black_solid.80">
                    Send Rate Confirmation
                  </Typography>
                }
              />
              <RateConfirmationModal
                open={rateConOpen}
                handleClose={handleClose}
                rateConData={rateConData}
                load={load}
                carrier={carrier}
                carrierContacts={carrierContacts}
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <Box>
              <PrimaryTextField
                label="Line Haul"
                value={linehaul}
                onChange={setLinehaul}
                onBlur={calculateTotalRate}
                width="190px"
                onEnter={handleConfirmBooking}
                isCurrency
                disabled={isLoading}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <PrimaryTextField
                label="FSC"
                value={fuelSurcharge}
                onChange={setFuelSurcharge}
                onBlur={calculateTotalRate}
                width="190px"
                onEnter={handleConfirmBooking}
                isCurrency
                disabled={isLoading}
                error={!fuelSurchargeValid}
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <PrimarySelect
              label="Dispatcher *"
              options={dispatchers}
              onChange={setSelectedDispatcher}
              value={selectedDispatcher}
              nameKey="userIdMcld"
              valueKey="userIdMcld"
              width="400px"
              disabled={isLoading}
            />
          </Box>
        </Box>
        <Box sx={{ marginLeft: "20px" }}>
          <Box className="Load-modal-input-row">
            <Typography variant="h3" color="primary.60">
              Contact Info
            </Typography>
          </Box>
          <Box className="Load-modal-input-row">
            <Box>
              <PrimarySelect
                label="Carrier Contact *"
                options={carrierContacts}
                onChange={setSelectedContactId}
                value={selectedContactId}
                nameKey="name"
                valueKey="id"
                width="190px"
                disabled={isLoading}
                error={!selectedContactId}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <PrimaryTextField
                label="Contact Phone *"
                value={contactPhone}
                onChange={setContactPhone}
                width="190px"
                onEnter={handleConfirmBooking}
                isPhone
                disabled={isLoading}
                error={!contactPhone}
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <PrimaryTextField
              label="Carrier Email *"
              value={contactEmail}
              onChange={setContactEmail}
              width="400px"
              onEnter={handleConfirmBooking}
              isEmail
              error={!emailValid || !contactEmail}
              setError={setEmailValid}
              disabled={isLoading}
            />
          </Box>
          <Box className="Load-modal-input-row">
            <Box>
              <PrimaryTextField
                label="Driver Name"
                value={driverName}
                onChange={setDriverName}
                width="190px"
                onEnter={handleConfirmBooking}
                disabled={isLoading}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <PrimaryTextField
                label="Driver Phone"
                value={driverPhone}
                onChange={setDriverPhone}
                width="190px"
                onEnter={handleConfirmBooking}
                isPhone
                disabled={isLoading}
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <Box>
              <PrimaryTextField
                label="Tractor #"
                value={tractorNumber}
                onChange={setTractorNumber}
                width="190px"
                onEnter={handleConfirmBooking}
                disabled={isLoading}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <PrimaryTextField
                label="Trailer #"
                value={trailerNumber}
                onChange={setTrailerNumber}
                width="190px"
                onEnter={handleConfirmBooking}
                disabled={isLoading}
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <PrimaryTextField
              label="Pro / Order Confirmation Number"
              value={confirmationNumber}
              onChange={setConfirmationNumber}
              width="400px"
              onEnter={handleConfirmBooking}
              disabled={isLoading}
              maxLength="50"
            />
          </Box>
          <Box>
            <PrimaryTextField
              label="Offer Comments"
              value={offerComments}
              onChange={setOfferComments}
              width="400px"
              onEnter={handleConfirmBooking}
              multiline
              rows={4}
              disabled={isLoading}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", width: "820px" }}>
        <Box>
          {!carrierQualified && carrier && !isLoading &&
            <WarningMessage text={carrierWarningText} displayText={carrierWarningText} />
          }
          {loadWarningText && !isLoading &&
            <WarningMessage text={loadWarningText} displayText={loadWarningText} />
          }
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
          <SecondaryButton
            label="Cancel"
            onClick={handleCancel}
            disabled={isLoading}
          />
          <Box sx={{ marginLeft: "20px" }}>
            <PrimaryButton
              label="Confirm"
              onClick={handleConfirmBooking}
              isLoading={isLoading}
              disabled={submitDisabled || isLoading}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

LoadBookingModal.propTypes = {
  handleRecentlyViewed: PropTypes.func.isRequired
};